import { client as heyApiClient } from "@/backend-sdk/client/client.gen";
import { createClient, type Client } from "@/backend-sdk/client/client";
import type { TokenResponse } from "@/backend-sdk/client/types.gen";
import { zTokenResponse } from "@/backend-sdk/client/zod.gen";
import type { FilterQueryParams } from "@/features/filters/filter.types";
import { toast } from "@/hooks/use-toast";
import { setGlobalSyncStatus } from "@/lib/sync-status-bus";
import { authDbg } from "@/lib/auth-debug-logger";

const AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/2fa/verify",
  "/auth/checkout-data",
  "/auth/refresh",
  "/auth/logout",
] as const;

/**
 * <M-247> O cabeçalho com que uma chamada declara que o `403` dela é uma **resposta**, e
 * não uma falha.
 *
 * Existe porque o interceptor abaixo transforma todo erro com mensagem em toast vermelho,
 * e há chamadas cujo `403` é o resultado esperado: `GET /my-account/users` responde
 * `403 Apenas administradores…` a quem só lê, e a Minha Conta usa essa resposta para
 * decidir o que desenhar. `SILENT_ENDPOINTS` não serve para isso — ele é por caminho, e
 * calaria também o `403` de um `POST` para o mesmo `/my-account/users`, que é falha de
 * verdade. Aqui quem declara é a chamada.
 *
 * Vai junto na requisição, como `x-ignore-global-filters`, porque é no par
 * (requisição, resposta) do interceptor que ele é lido. O servidor o ignora.
 */
export const CABECALHO_403_ESPERADO = "x-expected-403";

const SILENT_ENDPOINTS = [
  "/sync/status",
  "/admin/settings/maintenance-mode",
  // Polling do chat: falhas transitórias são reprocessadas em silêncio no
  // próximo tick; não devem gerar toast a cada ~1s.
  "/media-chat/jobs",
] as const;

type SyncStatusPayload = {
  sync_status?: {
    is_syncing?: boolean;
    source?: string | null;
  };
};

interface WindowWithApiDebug extends Window {
  __API_BASE_URL__?: string;
}

export interface HeyApiError extends Error {
  status: number;
  detail?: unknown;
  data?: unknown;
  response: Response;
}

const getRuntimeHostname = () =>
  typeof window !== "undefined" ? window.location.hostname : "localhost";

const rewriteLocalhostToRuntimeHost = (rawUrl: string) => {
  try {
    const runtimeHost = getRuntimeHostname();
    const url = new URL(rawUrl);
    if (
      (url.hostname === "localhost" || url.hostname === "127.0.0.1") &&
      runtimeHost !== "localhost"
    ) {
      url.hostname = runtimeHost;
    }
    return url.toString().replace(/\/$/, "");
  } catch {
    return rawUrl;
  }
};

const API_BASE_URL = (() => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) {
    return import.meta.env.DEV
      ? rewriteLocalhostToRuntimeHost(envUrl)
      : envUrl.replace(/\/$/, "");
  }

  if (import.meta.env.DEV) {
    return "http://localhost:8001";
  }

  throw new Error("VITE_API_URL is required in production");
})();

if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as WindowWithApiDebug).__API_BASE_URL__ = API_BASE_URL;
}

const getAccessToken = () => localStorage.getItem("access_token");
const getRefreshToken = () => localStorage.getItem("refresh_token");

export const setHeyApiTokens = (tokens: TokenResponse) => {
  localStorage.setItem("access_token", tokens.access_token);
  localStorage.setItem("refresh_token", tokens.refresh_token);
};

export const clearHeyApiTokens = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

export const getHeyApiSubAccountToken = () =>
  localStorage.getItem("subaccount_access_token");

export const setHeyApiSubAccountToken = (token: string) => {
  localStorage.setItem("subaccount_access_token", token);
};

export const clearHeyApiSubAccountToken = () => {
  localStorage.removeItem("subaccount_access_token");
};

/**
 * <D-062> A loja ativa da tela, que as rotas recebem em `?customer_uuid=`.
 *
 * O tenant é o `accounts.id`, e ele vive em `active_customer_uuid` desde que o contexto de
 * contas escolheu a loja. Mandá-lo é o caminho preferido: ele passa pelo guard da API a cada
 * requisição, enquanto o claim do token vale para leitura até o token vencer.
 */
export const tenantAtivo = (): string | undefined =>
  localStorage.getItem("active_customer_uuid") ?? undefined;

/**
 * <D-062> A **conta ativa** que o token em uso declara — o claim `account_id`.
 *
 * `POST /accounts/switch-account` é quem o grava, e é ele que resolve o tenant das rotas que
 * não recebem `customer_uuid` (conectar integração, listar contas externas, estado de
 * conexão). Um token de login comum não o traz: quem acabou de entrar ainda não escolheu
 * loja, e a API responde `400 Conta não informada`.
 *
 * Serve para o contexto de contas saber se precisa pedir um token novo ao restaurar a loja
 * que estava no navegador — restaurar do `localStorage` põe a loja na tela, mas não no token.
 *
 * `null` quando não há token, quando ele não decodifica ou quando não há claim.
 */
export const contaAtivaDoToken = (): string | null => {
  const token = getHeyApiSubAccountToken() ?? getAccessToken();
  if (!token) return null;
  try {
    const payload: unknown = JSON.parse(atob(token.split(".")[1] ?? ""));
    const conta =
      typeof payload === "object" && payload !== null
        ? (payload as { account_id?: unknown }).account_id
        : undefined;
    return typeof conta === "string" && conta !== "" ? conta : null;
  } catch {
    return null;
  }
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const getTokenForUrl = (url?: string): string | undefined => {
  if (!url) return getAccessToken() ?? undefined;

  // <D-089> As rotas de `/external-accounts` recebem `customer_uuid` e seguem com o token
  // principal — `selected` inclusive. Ela lia a loja só do token de conta, que vence sem ser
  // renovado, e o painel parado passava a responder `400 Conta não informada`.
  const adminUrls = ["/accounts", "/users/me/accounts", "/external-accounts"];
  if (adminUrls.some((adminUrl) => url.includes(adminUrl))) {
    return getAccessToken() ?? undefined;
  }

  const subAccountToken = getHeyApiSubAccountToken();
  const subTokenExpired = subAccountToken
    ? isTokenExpired(subAccountToken)
    : true;
  if (subAccountToken && !subTokenExpired) {
    authDbg(
      `[AUTH_DBG] getTokenForUrl using subaccount token for ${url?.slice(0, 60)} (sub token expiry check passed)`,
    );
    return subAccountToken;
  }

  const mainToken = getAccessToken();
  const mainTokenExpired = mainToken ? isTokenExpired(mainToken) : true;
  console.log(
    `[AUTH_DBG] getTokenForUrl for ${url?.slice(0, 60)}: subToken=${!!subAccountToken}${subAccountToken ? `(exp=${subTokenExpired})` : ""} mainToken=${!!mainToken}${mainToken ? `(exp=${mainTokenExpired})` : ""} → ${mainToken ? (mainTokenExpired ? "EXPIRED" : "returning main") : "NO_TOKEN"}`,
  );

  return getAccessToken() ?? undefined;
};

let heyApiGlobalFilterParams: FilterQueryParams | null = null;
let refreshPromise: Promise<TokenResponse> | null = null;
let isConfigured = false;

let _resolveAuthReady: (() => void) | null = null;
const _authReadyPromise = new Promise<void>((resolve) => {
  _resolveAuthReady = resolve;
});

export const setAuthReady = () => {
  _resolveAuthReady?.();
};

export const setHeyApiGlobalFilterParams = (
  params: FilterQueryParams | null,
) => {
  heyApiGlobalFilterParams = params;
};

const urlMatchesEndpoint = (
  url: string | undefined,
  endpoints: readonly string[],
) => {
  if (!url) return false;
  return endpoints.some((endpoint) => url.includes(endpoint));
};

const readResponseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.clone().json();
  } catch {
    return undefined;
  }
};

const extractErrorDetail = (data: unknown): unknown => {
  if (data && typeof data === "object" && "detail" in data) {
    return (data as { detail?: unknown }).detail;
  }
  return undefined;
};

const extractErrorMessage = (detail: unknown): string | undefined => {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) {
    const message = (detail as { message?: unknown }).message;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
};

const handleSyncStatusPayload = (data: unknown) => {
  const sync = (data as SyncStatusPayload | undefined)?.sync_status;
  if (sync && typeof sync.is_syncing === "boolean") {
    setGlobalSyncStatus(!!sync.is_syncing, sync.source ?? null);
  }
};

const createHeyApiError = (response: Response, data: unknown): HeyApiError => {
  const detail = extractErrorDetail(data);
  const message =
    extractErrorMessage(detail) ??
    `Request failed with status ${response.status}`;
  const error = new Error(message) as HeyApiError;
  error.name = "HeyApiError";
  error.status = response.status;
  error.detail = detail;
  error.data = data;
  error.response = response;
  return error;
};

export const isHeyApiError = (error: unknown): error is HeyApiError => {
  return (
    error instanceof Error &&
    error.name === "HeyApiError" &&
    "response" in error
  );
};

/**
 * Por que a sessão terminou sem o usuário pedir. A tela de login lê o motivo uma vez, para
 * dizer o que aconteceu em vez de aparecer do nada.
 */
export type MotivoDoLogoutAutomatico = "sessao-expirada" | "conta-inativa";

export const CHAVE_DO_MOTIVO_DO_LOGOUT = "motivo_do_logout_automatico";

/**
 * O `detail` com que a API recusa, a cada requisição, o token de um usuário desativado
 * (`contaInativa` no `pde-api-server`). O login deixa a conta inativa entrar de propósito
 * e é esta resposta que a barra — e o `/auth/refresh` também não olha `is_active`, então
 * renovar não resolve: sem tratá-la aqui, o usuário ficava na tela colecionando toasts.
 */
const DETALHE_CONTA_INATIVA = "User account is inactive";

/**
 * Os `detail` com que a API recusa a **credencial** (`authErrors.ts` no `pde-api-server`).
 *
 * O status sozinho não basta: a API também responde `401` a um dado errado dentro de uma
 * sessão válida — `Invalid password` e `Invalid 2FA code` ao desligar o 2FA ou gerar
 * códigos de backup. Tratados como sessão recusada, eles renovavam o token, repetiam a
 * tentativa (contando duas vezes no limite do 2FA) e, com o logout automático, derrubariam
 * quem só errou a senha. Achado de code review.
 */
const DETALHES_DE_CREDENCIAL_RECUSADA: readonly string[] = [
  "Not authenticated",
  "Could not validate credentials",
  "Token inválido ou expirado",
];

/**
 * As telas de quem ainda não entrou. O `AuthProvider` carrega o perfil em todas as rotas, e
 * um token velho guardado no navegador seria recusado também aqui: navegar para `/login`
 * arrancaria o usuário do cadastro ou da recuperação de senha que ele abriu. Nelas a sessão
 * recusada só é limpa. Achado de code review.
 */
const TELAS_PUBLICAS: readonly string[] = [
  "/login",
  "/register",
  "/forgot-password",
  "/2fa",
];

/** O aviso de quem perdeu a sessão — no painel, na hora, e na tela de login, depois. */
export const AVISO_DE_SESSAO_EXPIRADA = {
  title: "Sua sessão expirou",
  description: "Relogue usando suas credenciais.",
} as const;

let encerrandoSessao = false;

/**
 * O logout automático: a API recusou a credencial e não há como renová-la.
 *
 * Limpa o mesmo que o logout pedido pelo usuário (`localStorage.clear()` em
 * `auth.context`), e não só o par de tokens: o token de conta, a loja ativa e o contexto
 * de acesso da sessão recusada não podem sobreviver para a próxima pessoa que entrar
 * neste navegador. A navegação recarrega a página, e é isso que zera o estado do React.
 *
 * Nas telas públicas ela só limpa — ver `TELAS_PUBLICAS`. Na `/login`, em particular, é o
 * próprio login que acabou de gravar um token recusado, e a tela já mostra o erro dele.
 */
export const encerrarSessaoRecusada = (motivo: MotivoDoLogoutAutomatico) => {
  if (typeof window === "undefined" || encerrandoSessao) return;

  localStorage.clear();
  if (TELAS_PUBLICAS.includes(window.location.pathname)) return;

  // Várias requisições em voo recebem a mesma recusa; só a primeira navega.
  encerrandoSessao = true;
  try {
    sessionStorage.setItem(CHAVE_DO_MOTIVO_DO_LOGOUT, motivo);
  } catch {
    /* sem o aviso, o logout acontece igual */
  }
  window.location.replace("/login");
};

/** Lê — e consome — o motivo gravado por `encerrarSessaoRecusada`. */
export const consumirMotivoDoLogoutAutomatico =
  (): MotivoDoLogoutAutomatico | null => {
    try {
      const motivo = sessionStorage.getItem(CHAVE_DO_MOTIVO_DO_LOGOUT);
      sessionStorage.removeItem(CHAVE_DO_MOTIVO_DO_LOGOUT);
      return motivo === "sessao-expirada" || motivo === "conta-inativa"
        ? motivo
        : null;
    } catch {
      return null;
    }
  };

class RefreshTokenAusente extends Error {
  constructor() {
    super("refresh_token ausente");
    this.name = "RefreshTokenAusente";
  }
}

/**
 * A renovação falhou porque a API **recusou** o refresh token, e não porque não foi possível
 * perguntar. Só a recusa encerra a sessão: rede fora do ar, `5xx` ou o `429` do limite de
 * tentativas derrubavam o usuário antes, e nada disso diz que a credencial deixou de valer.
 */
const renovacaoRecusada = (erro: unknown): boolean => {
  if (erro instanceof RefreshTokenAusente) return true;
  if (!isHeyApiError(erro)) return false;
  return (
    erro.status >= 400 &&
    erro.status < 500 &&
    erro.status !== 408 &&
    erro.status !== 429
  );
};

const ehContaInativa = async (response: Response): Promise<boolean> => {
  if (response.status !== 403) return false;
  const detalhe = extractErrorDetail(await readResponseJson(response));
  return detalhe === DETALHE_CONTA_INATIVA;
};

const ehCredencialRecusada = async (response: Response): Promise<boolean> => {
  if (response.status !== 401) return false;
  const detalhe = extractErrorDetail(await readResponseJson(response));
  return (
    typeof detalhe === "string" &&
    DETALHES_DE_CREDENCIAL_RECUSADA.includes(detalhe)
  );
};

const refreshAccessToken = async (): Promise<TokenResponse> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new RefreshTokenAusente();
  }

  const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
    body: JSON.stringify({ refresh_token: refreshToken }),
    headers: {
      "Content-Type": "application/json",
      "x-ignore-global-filters": "1",
    },
    method: "POST",
  });

  const data: unknown = await response.json();
  if (!response.ok) {
    throw createHeyApiError(response, data);
  }

  return zTokenResponse.parse(data);
};

/**
 * `new Request` preservando o `signal` de quem chamou, e sem quebrar onde ele não é aceito.
 *
 * <M-252> Os dois pontos que reconstroem a requisição — o retry do `401` e o acréscimo dos
 * filtros globais — precisam levar o `AbortSignal` adiante: sem ele o `AbortController` de
 * quem chamou passa a apontar para uma requisição que não existe mais, e cancelar não
 * cancela nada (é o caso da chamada de LLM da tela de Insights, abortada a cada troca de
 * filtro). O `try` existe porque o `Request` do jsdom, na suíte, recusa o sinal que ele
 * mesmo criou — e uma reconstrução que **lança** é pior do que uma que perde o
 * cancelamento. No navegador o ramo de baixo não é alcançado.
 */
const reconstruirRequest = (
  url: string,
  init: RequestInit,
  signal: AbortSignal | null,
): Request => {
  if (signal) {
    try {
      return new Request(url, { ...init, signal });
    } catch {
      /* ambiente que não aceita este sinal — segue sem cancelamento propagado */
    }
  }
  return new Request(url, init);
};

/**
 * Exportada para teste: é nela que o `401` vira renovação e a recusa vira logout.
 */
export const createHeyApiFetch = (): typeof fetch => {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = input instanceof Request ? input : new Request(input, init);
    // <M-252> O corpo é lido **antes** do `fetch`, e é ele que torna a repetição possível:
    // consumir um `Request` inutiliza o stream dele, então o `request.clone()` que só
    // acontecia depois da resposta lançava `TypeError: unusable` em toda requisição com
    // corpo. Na prática, quem tivesse o token expirado ao trocar a foto via um erro de
    // "verifique o tamanho e formato do arquivo" em vez da repetição. Achado de code
    // review; a leitura é de bytes, e não de texto, pelo mesmo motivo de
    // `appendHeyApiGlobalFilters`.
    const corpoParaRepetir =
      request.method === 'GET' || request.method === 'HEAD'
        ? undefined
        : await request.clone().arrayBuffer();
    const response = await fetch(request);
    const isAuthEndpoint = urlMatchesEndpoint(request.url, AUTH_ENDPOINTS);

    // Só a requisição que levou uma credencial diz alguma coisa sobre a sessão: sem
    // `Authorization`, o `401` é de quem nunca entrou, e não há o que encerrar.
    if (isAuthEndpoint || !request.headers.has("Authorization")) {
      return response;
    }

    if (await ehContaInativa(response)) {
      encerrarSessaoRecusada("conta-inativa");
      return response;
    }

    if (!(await ehCredencialRecusada(response))) {
      return response;
    }

    authDbg(
      `[AUTH_DBG] createHeyApiFetch: got 401 for ${request.method} ${request.url?.slice(0, 80)} refreshing...`,
    );

    let newTokens: TokenResponse;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken()
          .then((newTokens) => {
            authDbg(
              "[AUTH_DBG] createHeyApiFetch: refresh succeeded, token refreshed",
            );
            setHeyApiTokens(newTokens);
            return newTokens;
          })
          .finally(() => {
            refreshPromise = null;
          });
      } else {
        authDbg(
          "[AUTH_DBG] createHeyApiFetch: refresh already in-flight, joining...",
        );
      }

      newTokens = await refreshPromise;
    } catch (refreshError) {
      if (renovacaoRecusada(refreshError)) {
        authDbg(
          `[AUTH_DBG] createHeyApiFetch: refresh rejected for ${request.url?.slice(0, 80)} ending session`,
        );
        encerrarSessaoRecusada("sessao-expirada");
      }
      throw refreshError;
    }

    // <M-252> Os cabeçalhos são **copiados** e então alterados. Passar `headers` ao
    // construtor esvazia a lista em vez de fundi-la, e a requisição repetida saía só com
    // o `Authorization`: sem o `Content-Type`, ou seja, sem o `boundary` que separa as
    // partes do multipart (`422`), e sem `x-ignore-global-filters` nem o cabeçalho de
    // `403` esperado de M-247. Achado de code review.
    const cabecalhos = new Headers(request.headers);
    cabecalhos.set('Authorization', `Bearer ${newTokens.access_token}`);

    authDbg(
      `[AUTH_DBG] createHeyApiFetch: retrying ${request.method} ${request.url?.slice(0, 80)} with new token`,
    );
    const repetida = await fetch(
      reconstruirRequest(
        request.url,
        {
          body: corpoParaRepetir,
          headers: cabecalhos,
          method: request.method,
        },
        request.signal,
      ),
    );

    // A API recusou até o token que ela mesma acabou de emitir: não há o que renovar.
    if (await ehCredencialRecusada(repetida)) {
      encerrarSessaoRecusada("sessao-expirada");
    } else if (await ehContaInativa(repetida)) {
      encerrarSessaoRecusada("conta-inativa");
    }
    return repetida;
  };
};

const shouldIgnoreGlobalFilters = (request: Request) => {
  return request.headers.has("x-ignore-global-filters");
};

/**
 * Exportada para teste: é ela que reconstrói a requisição, e <M-252> foi um corpo binário
 * destruído nessa reconstrução. O teste que prova que ele atravessa intacto precisa
 * alcançá-la sem subir o cliente inteiro.
 */
export const appendHeyApiGlobalFilters = async (
  request: Request,
): Promise<Request> => {
  if (!heyApiGlobalFilterParams || shouldIgnoreGlobalFilters(request)) {
    return request;
  }

  const url = new URL(request.url);
  Object.entries(heyApiGlobalFilterParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && !url.searchParams.has(key)) {
      url.searchParams.set(key, String(value));
    }
  });

  // <M-252> `arrayBuffer()`, e **não** `text()`. Este ramo reconstrói a requisição só para
  // acrescentar os filtros à query, e o corpo passa por ele de carona; lido como texto, ele
  // é decodificado como UTF-8 e recodificado, o que é inofensivo para JSON — que é UTF-8 —
  // e destrói qualquer corpo binário: cada byte ≥ 0x80 vira `U+FFFD`. O upload de avatar,
  // que é `multipart/form-data`, chegava ao servidor com o PNG assim: 198 bytes viravam
  // 369, e o `0x89` da assinatura, `EF BF BD`. Ninguém tinha notado porque, até M-020, o
  // servidor validava o arquivo e o descartava. O `Blob` atravessa sem ser interpretado.
  const corpo = await request.clone().arrayBuffer();

  return reconstruirRequest(
    url.toString(),
    {
      // Um `POST` sem corpo produz um buffer vazio, e mandá-lo mudaria o `Content-Length`
      // de ausente para `0`. `undefined` mantém a requisição como ela era.
      body: corpo.byteLength > 0 ? corpo : undefined,
      // O `Content-Type` original vem junto, e para o multipart isso é o que preserva o
      // `boundary` que o navegador gerou — sem ele o servidor não separa as partes.
      headers: request.headers,
      method: request.method,
    },
    request.signal,
  );
};

const attachHeyApiMiddleware = (
  client: Client,
  options: { admin: boolean },
) => {
  client.interceptors.request.use(async (request) => {
    const token = options.admin
      ? (getAccessToken() ?? undefined)
      : getTokenForUrl(request.url);

    if (!token) {
      await _authReadyPromise;
    }

    const updatedToken = options.admin
      ? (getAccessToken() ?? undefined)
      : getTokenForUrl(request.url);

    if (updatedToken) {
      request.headers.set("Authorization", `Bearer ${updatedToken}`);
    }

    return appendHeyApiGlobalFilters(request);
  });

  client.interceptors.response.use(async (response, request) => {
    const data = await readResponseJson(response);
    handleSyncStatusPayload(data);

    if (response.ok) {
      return response;
    }

    const isAuthEndpoint = urlMatchesEndpoint(request.url, AUTH_ENDPOINTS);
    const isSilentEndpoint = urlMatchesEndpoint(request.url, SILENT_ENDPOINTS);

    if (response.status === 403 && isSilentEndpoint && !getAccessToken()) {
      return response;
    }

    // <M-247> O `403` que a própria chamada declarou esperar. Ver `CABECALHO_403_ESPERADO`.
    if (
      response.status === 403 &&
      request.headers.has(CABECALHO_403_ESPERADO)
    ) {
      return response;
    }

    // <M-280> O `402` é só o saldo de IA insuficiente (chat e auditoria), e as duas telas o
    // trocam por um aviso com a oferta de compra: o toast vermelho em cima dele diria "erro"
    // para o que não é.
    if (response.status === 402) {
      return response;
    }

    const detail = extractErrorDetail(data);
    const message = extractErrorMessage(detail);

    // A credencial recusada é o logout automático acontecendo, e não um erro da tela: o
    // `detail` em inglês (`Not authenticated`) virava um toast "Erro" sem explicação.
    //
    // Só avisa quem **levou** credencial — a mesma regra de `createHeyApiFetch`. Sem
    // `Authorization` não havia sessão para expirar: é quem ainda não entrou, e a `/login`
    // faz uma chamada assim ao abrir (a checagem de manutenção, rota de admin que responde
    // `401 Not authenticated` a quem não tem token). Numa aba anônima o aviso de sessão
    // expirada aparecia antes de qualquer login. Nesse caso não há toast nenhum.
    if (
      !isAuthEndpoint &&
      response.status === 401 &&
      message &&
      DETALHES_DE_CREDENCIAL_RECUSADA.includes(message)
    ) {
      if (request.headers.has("Authorization")) {
        toast(AVISO_DE_SESSAO_EXPIRADA);
      }
      return response;
    }

    // Suppress toast for plan-gated 403 errors — handled by FeatureGate component
    if (
      message &&
      !isAuthEndpoint &&
      !message.includes("is not available on your plan")
    ) {
      toast({
        title: "Erro",
        description: message,
        variant: "destructive",
      });
    }

    return response;
  });

  client.interceptors.error.use((error, response) => {
    if (!response) {
      return error;
    }
    return createHeyApiError(response, error);
  });
};

export const heyAdminApiClient = createClient({
  baseUrl: API_BASE_URL,
  fetch: createHeyApiFetch(),
});

export const configureHeyApiClient = () => {
  if (isConfigured) {
    return;
  }

  heyApiClient.setConfig({
    baseUrl: API_BASE_URL,
    fetch: createHeyApiFetch(),
  });

  attachHeyApiMiddleware(heyApiClient, { admin: false });
  attachHeyApiMiddleware(heyAdminApiClient, { admin: true });
  isConfigured = true;
};

export { heyApiClient };
